import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getSessionUser } from '@/lib/session';
import { normalizeRole } from '@/auth/permissions';
import { getHomePathForRole } from '@/utils/roleRouting';
import { toast } from '@/lib/toast';
import { canManageClientPortalBilling, canManageClientPortalSales } from './clientPortalPilotAccess';
import { isPersonalDotsOnlyUser } from './dotsAccess';

export function RequireAdmin() {
  const location = useLocation();
  const user = getSessionUser();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const role = normalizeRole(user.role);
  if (location.pathname.startsWith('/admin/rh/dots') && isPersonalDotsOnlyUser(user)) {
    toast('Tu cuenta utiliza DOTS personal. Desde ahí puedes evaluar y consultar tu feedback.', 'warning');
    return <Navigate to="/daily" replace state={{ from: location }} />;
  }
  if (location.pathname.startsWith('/admin/ventas/kilometraje') && role !== 'ADMIN') {
    toast('Kilometraje está disponible únicamente para ADMIN.', 'warning');
    return <Navigate to={getHomePathForRole(role)} replace state={{ from: location }} />;
  }
  const isSupportOwner = (user.email ?? '').trim().toLowerCase() === 'sergio@sien.com';
  if (isSupportOwner && location.pathname.startsWith('/admin/soporte')) {
    return <Outlet />;
  }
  const allowed = ['ADMIN', 'GERENTE_INV', 'VENTAS_INTERNAS', 'COBRANZA', 'RH', 'MKT'];
  if (!allowed.includes(role)) {
    // Non-admin users are redirected to a safe default.
    if (role === 'OFICINA') {
      toast('No tienes acceso a Administración', 'warning');
    }
    const fallback = role === 'CONSULTA' ? '/inventario' : getHomePathForRole(role);
    return <Navigate to={fallback} replace state={{ from: location }} />;
  }

  if (role === 'RH') {
    const path = location.pathname;
    const allowedPath =
      path === '/admin' ||
      path === '/admin/rh' ||
      path.startsWith('/admin/rh/') ||
      path === '/admin/usuarios' ||
      path.startsWith('/admin/usuarios/');
    if (!allowedPath) {
      toast('No tienes acceso a esta sección de Administración.', 'warning');
      return <Navigate to="/admin/rh" replace state={{ from: location }} />;
    }
  }

  if (role === 'VENTAS_INTERNAS') {
    const path = location.pathname;
    const allowedSalesPath =
      path === '/admin' ||
      path === '/admin/cliente-portal' ||
      path.startsWith('/admin/cliente-portal/actividad-existencias') ||
      path.startsWith('/admin/cliente-portal/quote-intake') ||
      path.startsWith('/admin/cliente-portal/order-requests');
    if (!allowedSalesPath) {
      toast('No tienes acceso a esta sección de Administración.', 'warning');
      return <Navigate to="/admin/cliente-portal" replace state={{ from: location }} />;
    }
  }

  if (role === 'COBRANZA') {
    const path = location.pathname;
    const allowedBillingPath =
      path === '/admin' ||
      path === '/admin/cliente-portal' ||
      path.startsWith('/admin/cliente-portal/invoice-requests');
    if (!allowedBillingPath) {
      toast('No tienes acceso a esta sección de Administración.', 'warning');
      return <Navigate to="/admin/cliente-portal" replace state={{ from: location }} />;
    }
  }

  if (role === 'MKT') {
    const path = location.pathname;
    if (!path.startsWith('/admin/ventas/contenido')) {
      toast('Marketing sólo tiene acceso a la gestión de contenido comercial.', 'warning');
      return <Navigate to="/admin/ventas/contenido" replace state={{ from: location }} />;
    }
  }

  if (
    location.pathname.startsWith('/admin/cliente-portal/quote-intake') ||
    location.pathname.startsWith('/admin/cliente-portal/order-requests')
  ) {
    if (!canManageClientPortalSales(user.email, user.role)) {
      toast('No tienes acceso a esta sección del portal cliente.', 'warning');
      return <Navigate to={getHomePathForRole(role)} replace state={{ from: location }} />;
    }
  }

  if (location.pathname.startsWith('/admin/cliente-portal/invoice-requests')) {
    if (!canManageClientPortalBilling(user.email, user.role)) {
      toast('No tienes acceso a esta sección del portal cliente.', 'warning');
      return <Navigate to={getHomePathForRole(role)} replace state={{ from: location }} />;
    }
  }

  if (role === 'GERENTE_INV') {
    const path = location.pathname;
    const allowedExact = new Set(['/admin', '/admin/inventario', '/admin/productos', '/admin/movimientos']);
    const allowedPrefix = ['/admin/etiquetas'];
    const allowedPath =
      allowedExact.has(path) || allowedPrefix.some(prefix => path.startsWith(prefix));
    if (!allowedPath) {
      toast('No tienes acceso a esta sección de Administración.', 'warning');
      return <Navigate to="/admin/inventario" replace state={{ from: location }} />;
    }
  }

  return <Outlet />;
}

export default RequireAdmin;
